import { Suspense, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useMatch } from 'react-router';
import { LoadingDisplay } from './LoadingDisplay';
import metrMark from '../assets/metr-mark.svg';
import { config } from '../config/env';
import { rememberListLocation } from '../utils/returnTo';
import { ThemePicker } from './ThemePicker';
import { UserMenu } from './UserMenu';

const NAV_ITEMS = [
  { path: '/jobs', label: 'Jobs' },
  { path: '/eval-sets', label: 'Eval Sets' },
  { path: '/samples', label: 'Samples' },
  { path: '/scans', label: 'Scans' },
  { path: '/usage', label: 'Usage' },
  { path: '/search', label: 'Transcript Search' },
];

/**
 * The one saturated surface (the header) over the ground. Pages compose their
 * own panes inside <main>, so a page can be one list pane or several panes
 * side by side without fighting a wrapper.
 */
export function Layout() {
  const location = useLocation();
  // Each navigation gets a new location, so its menu starts closed.
  const [menuLocation, setMenuLocation] = useState<typeof location | null>(
    null
  );
  const menuOpen = menuLocation === location;
  const menuButton = useRef<HTMLButtonElement>(null);
  const runPage = useMatch('/eval-set/:evalSetId/:view');
  // Pages that size their pane only from `md` up (`md:h-full`): on a phone the
  // whole page scrolls instead, or their content is cut off.
  const settingsPage = useMatch('/settings/*');
  const scrollPage =
    settingsPage !== null ||
    ['status', 'approvals'].includes(runPage?.params.view ?? '');
  // Run pages return to the list view the run was opened from.
  useEffect(() => {
    rememberListLocation(location.pathname, location.search);
  }, [location.pathname, location.search]);
  useEffect(() => {
    if (!menuOpen) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuLocation(null);
        menuButton.current?.focus();
      }
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [menuOpen]);

  return (
    <div
      className={`flex h-dvh flex-col overflow-hidden bg-ground ${scrollPage ? 'max-md:overflow-y-auto' : ''}`}
    >
      <header className="grid shrink-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 bg-header px-3 text-on-header sm:px-[18px] xl:grid-cols-[auto_minmax(0,1fr)_auto]">
        <Brand />
        <button
          ref={menuButton}
          type="button"
          aria-label="Navigation menu"
          aria-expanded={menuOpen}
          aria-controls="header-menu"
          className="btn btn-on-header col-start-2 row-start-1 my-1 h-11 w-11 justify-self-end p-0 md:hidden"
          onClick={() => setMenuLocation(menuOpen ? null : location)}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path
              d={menuOpen ? 'M5 5l10 10M5 15L15 5' : 'M3 5h14M3 10h14M3 15h14'}
            />
          </svg>
        </button>
        <div
          id="header-menu"
          className={menuOpen ? 'contents' : 'hidden md:contents'}
        >
          <nav
            aria-label="Primary"
            className="col-span-2 row-start-2 grid min-w-0 grid-cols-2 gap-0.5 border-t border-white/10 py-1.5 md:flex md:flex-wrap md:items-center xl:col-span-1 xl:col-start-2 xl:row-start-1 xl:ml-1 xl:border-0 xl:py-0"
          >
            {NAV_ITEMS.map(item => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive ? 'page' : undefined}
                  className="nav-link shrink-0 max-md:min-h-11"
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="col-span-2 col-start-1 row-start-3 flex h-[52px] items-center justify-self-end gap-2 md:col-span-1 md:col-start-2 md:row-start-1 xl:col-start-3">
            <Link
              to="/launch"
              aria-label="New eval set"
              data-tip="New eval set"
              className="btn btn-on-header btn-icon tip shrink-0 no-underline"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M8 2.5v11M2.5 8h11" />
              </svg>
            </Link>
            <ThemePicker />
            <UserMenu />
          </div>
        </div>
      </header>

      <main
        className={`min-h-0 flex-1 p-3 ${scrollPage ? 'max-md:flex-none' : ''}`}
      >
        <Suspense
          fallback={
            <div className="pane h-full overflow-hidden">
              <LoadingDisplay />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}

// Organization marks bundled with the viewer, keyed by the deployment's
// `hawk:org`. Any other org (including the default) gets the wordmark alone.
const ORG_MARKS: Record<string, { src: string; alt: string }> = {
  metr: { src: metrMark, alt: 'METR' },
};

/**
 * Header identity: the organization's mark when one is bundled for
 * `config.org`, then the Hawk wordmark. Links back to the jobs list, so it
 * doubles as the way home from every Hawk-owned page.
 */
export function Brand({
  to = '/jobs',
  reload = false,
}: {
  to?: string;
  /** Full document load: from the embedded viewers, whose global CSS must not
   *  survive into Hawk's own pages. */
  reload?: boolean;
}) {
  const mark = ORG_MARKS[config.org];
  const className =
    'flex shrink-0 items-center gap-2.5 text-on-header no-underline';
  const content = (
    <>
      {mark && (
        <img
          src={mark.src}
          alt={mark.alt}
          title={mark.alt}
          className="h-[22px]"
        />
      )}
      <span className="text-base font-bold tracking-[-0.005em]">Hawk</span>
    </>
  );
  return reload ? (
    <a href={to} className={className}>
      {content}
    </a>
  ) : (
    <Link to={to} className={className}>
      {content}
    </Link>
  );
}
